import { ReactNode, forwardRef } from 'react';
import { Container, ContainerProps } from '@mantine/core';
import { usePreferencesStore, selectLayoutWidth, selectLayoutPadding } from '@/stores/preferencesStore';

interface LayoutContainerProps extends Omit<ContainerProps, 'fluid' | 'size'> {
  children: ReactNode;
  /** Override the preference (for pages that must be full-width) */
  forceFullWidth?: boolean;
}

// Custom padding values for full-width mode with bigger, more noticeable differences
// Uses responsive approach: smaller padding on mobile, full padding on larger screens
const PADDING_MAP = {
  xs: { base: 12, sm: 16, md: 20, lg: 24 },      // Extra Small - minimal spacing
  sm: { base: 16, sm: 24, md: 36, lg: 48 },      // Small - tight spacing
  md: { base: 20, sm: 32, md: 52, lg: 72 },      // Medium - comfortable spacing (default)
  lg: { base: 24, sm: 40, md: 68, lg: 96 },      // Large - generous spacing
  xl: { base: 28, sm: 48, md: 84, lg: 120 },     // Extra Large - very spacious
};

/**
 * LayoutContainer - Applies layout width preference to page content
 *
 * Automatically reads the user's layout width preference and applies appropriate Container sizing.
 *
 * - Boxed mode: Uses Container size="xl" (1320px max-width)
 * - Full-width mode: Uses Container fluid with responsive horizontal padding that scales from mobile to desktop
 * - Padding automatically reduces on smaller screens to preserve content space
 * - Use forceFullWidth prop for pages that require full-width regardless of preference (e.g., Board view)
 *
 * @example
 * ```tsx
 * // Standard usage
 * <LayoutContainer py="xl">
 *   <YourContent />
 * </LayoutContainer>
 *
 * // Force full-width (e.g., for Board view)
 * <LayoutContainer forceFullWidth>
 *   <KanbanBoard />
 * </LayoutContainer>
 * ```
 */
const LayoutContainer = forwardRef<HTMLDivElement, LayoutContainerProps>(
  ({ children, forceFullWidth = false, ...containerProps }, ref) => {
    const layoutWidth = usePreferencesStore(selectLayoutWidth);
    const layoutPadding = usePreferencesStore(selectLayoutPadding);

    // If forced full-width, render children directly without Container
    if (forceFullWidth) {
      return <>{children}</>;
    }

    // If preference is full-width, use fluid container with configurable padding
    if (layoutWidth === 'full-width') {
      return (
        <Container ref={ref} fluid px={PADDING_MAP[layoutPadding]} {...containerProps}>
          {children}
        </Container>
      );
    }

    // Default: boxed layout with xl size (1320px max-width)
    return (
      <Container ref={ref} size="xl" {...containerProps}>
        {children}
      </Container>
    );
  }
);

LayoutContainer.displayName = 'LayoutContainer';

export default LayoutContainer;
